import { templateBuilderQueries } from "@/features/template-builder/queries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  removeCachedVariables,
  upsertCachedVariable,
  upsertCachedVariables,
} from "@/features/template-builder/queries/cache";
import { templateBuilderMutations } from "@/features/template-builder/mutations";
import type {
  TemplateVariableInput,
  TemplateVariableTarget,
} from "@/domains/templates/types";

function mutationTarget(target: TemplateVariableTarget) {
  return target.level === "analysisstep"
    ? { templateId: target.templateId, stepId: target.stepId }
    : { templateId: target.templateId };
}

export function useTemplateVariableMutations(target: TemplateVariableTarget) {
  const queryClient = useQueryClient();
  const resourceTarget = mutationTarget(target);

  const refreshSteps = () =>
    target.level === "analysisstep"
      ? queryClient.invalidateQueries({
          ...templateBuilderQueries.steps(target.templateId),
          exact: true,
        })
      : Promise.resolve();
  const onError = async (error: unknown) => {
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      error.status === 409
    )
      await refreshSteps();
  };
  const createVariable = useMutation({
    mutationFn: ({ variable }: { variable: TemplateVariableInput }) =>
      templateBuilderMutations.createVariable({ ...resourceTarget, variable }),
    onError,
    onSuccess: async (variable) => {
      upsertCachedVariable(queryClient, target, variable);
      await refreshSteps();
    },
  });

  const updateVariable = useMutation({
    mutationFn: ({
      variableId,
      variable,
    }: {
      variableId: string;
      variable: TemplateVariableInput;
    }) =>
      templateBuilderMutations.updateVariable({
        ...resourceTarget,
        variableId,
        variable,
      }),
    onError,
    onSuccess: async (variable) => {
      upsertCachedVariable(queryClient, target, variable);
      await refreshSteps();
    },
  });

  const createVariableHierarchy = useMutation({
    mutationFn: ({ variable }: { variable: TemplateVariableInput }) =>
      templateBuilderMutations.createVariableHierarchy({
        ...resourceTarget,
        variable,
      }),
    onError,
    onSuccess: async (variables) => {
      upsertCachedVariables(queryClient, target, variables);
      await refreshSteps();
    },
  });

  const deleteVariable = useMutation({
    mutationFn: ({ variableId }: { variableId: string }) =>
      templateBuilderMutations.deleteVariable({
        ...resourceTarget,
        variableId,
      }),
    onError,
    onSuccess: async ({ deleted_variable_ids: variableIds }) => {
      removeCachedVariables(queryClient, target, variableIds);
      await refreshSteps();
    },
  });

  return {
    createVariable,
    createVariableHierarchy,
    updateVariable,
    deleteVariable,
  };
}
